import { useMemo, useRef } from 'react';

import useDataLayer, { useResultsAvailableEvent } from '@/hooks/useDataLayer';
import useDestination from '@/hooks/useDestination';
import {
  getPropertyDetails,
  getSearchDetails,
} from '@/utils/getDataLayerVariables';
import usePropertyContent from '../../../../hooks/usePropertyContent';
import useGetPackageOffers from '../../hooks/useGetPackageOffers';
import useProcuredOffer from '../../hooks/useProcuredOffer';

const DataLayer = ({ pageType }) => {
  const propertyContent = usePropertyContent();
  const { searchQuery, packageOffers, availability } = useGetPackageOffers();
  const destination = useDestination();
  const procuredOffer = useProcuredOffer();

  const propertyContentRef = useRef();
  const destinationRef = useRef();

  propertyContentRef.current = propertyContent;
  destinationRef.current = destination;

  const withSearchEvent = useMemo(() => {
    if (!searchQuery) {
      return null;
    }

    return {
      destinationName: destinationRef.current.title,
      event: 'pageView',
      pageType: `${pageType} With Search`,
      ...getPropertyDetails(propertyContentRef.current),
      ...getSearchDetails(searchQuery),
    };
  }, [pageType, searchQuery]);

  useDataLayer(withSearchEvent);

  const withoutSearchEvent = useMemo(
    () => ({
      destinationName: destination.title,
      ...getPropertyDetails(propertyContent),
      event: 'pageView',
      pageType,
    }),
    [pageType, propertyContent, destination],
  );

  useDataLayer(withoutSearchEvent);

  const extra = useMemo(
    () => ({
      destinationName: destination.title,
      pageType: `${pageType} With Search`,
      availability,
    }),
    [availability, pageType, destination],
  );

  useResultsAvailableEvent(
    searchQuery,
    pageType === 'Package Page' ? packageOffers : null,
    extra,
  );

  const onProcuredOfferView = useMemo(() => {
    if (!procuredOffer) {
      return null;
    }

    return {
      destinationName: destinationRef.current.title,
      event: 'pageView',
      pageType: `${pageType} With Procured Offer`,
      procuredOffer,
    };
  }, [procuredOffer, pageType]);

  useDataLayer(onProcuredOfferView);

  return null;
};

export default DataLayer;
